import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { getIsEditMode } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { Delete, Edit16, NegativeFill, OpenNewWindow16 } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { colors } from 'ui/theme';
import { IS_VALID_URL } from 'utils/validationRegex';
import { DISPLAY_CONTENT } from '../../constants';
import { EmptyInput, ReadOnlyField, formatLabelForId, getCharacterCountText } from '../../utils';
import {
  EditableLabel,
  InputFieldRow,
  LabelRow,
  LabelWrapper,
  RestrictionText,
  StyledFormField,
  StyledGhostButton,
  StyledInputText,
  StyledLink,
} from './styles';

const FIELD_LABEL_MAX_LENGTH = 25;
const { customTextFieldsDefaultLabel } = DISPLAY_CONTENT.settings;

type Props = {
  label: string;
  setLabel?: (label: string) => void;
  value: string;
  setValue: (value: string) => void;
  errorText?: ReactNode;
  hasError?: boolean;
  onDelete?: () => void;
  placeholder?: string;
};

export const InputTextField = ({
  label,
  setLabel,
  value,
  setValue,
  errorText,
  hasError,
  onDelete,
  placeholder,
}: Props) => {
  const testTag = `embedded-template-settings-form-${formatLabelForId(label)}`;
  const isInputUrl = IS_VALID_URL.test(value);

  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { view } = useQueryParamContext();
  const { isEditMode } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  /** `labelValue` stores label changes in InputTextField state until ready to `setLabel` on blur.
   *  Prevents automatic re-rendering of FormField upon label change. */
  const [labelValue, setLabelValue] = useState(label);
  /** `isEditingKey` should initialize to true when user adds custom field while template
   *  is in editing mode. For already existing fields, this will be false. */
  const [isEditingKey, setIsEditingKey] = useState(isEditMode);

  const handleBlur = () => {
    if (setLabel) setLabel(labelValue);
    setIsEditingKey(false);
  };

  useEffect(() => {
    if (setLabel && !isEditingKey && label.length === 0) setLabel(customTextFieldsDefaultLabel);
  }, [isEditingKey]);

  const Label = () =>
    !isEmbeddedMessagingEnabled && setLabel ? (
      <EditableLabel>
        {isEditingKey ? (
          <LabelWrapper>
            <InputText
              data-test="editable-label-input"
              maxLength={FIELD_LABEL_MAX_LENGTH}
              onBlur={handleBlur}
              onChange={setLabelValue}
              value={labelValue}
              autoFocus
            />
            <RestrictionText>
              {getCharacterCountText(labelValue, FIELD_LABEL_MAX_LENGTH)}
            </RestrictionText>
          </LabelWrapper>
        ) : (
          <LabelRow>
            {label}
            {isEditModeVisible && (
              <StyledGhostButton onClick={() => setIsEditingKey(true)}>
                <Edit16 />
              </StyledGhostButton>
            )}
          </LabelRow>
        )}
      </EditableLabel>
    ) : (
      <>{label}</>
    );

  const ReadOnlyValue = () =>
    isInputUrl ? (
      <StyledLink href={value} target="_blank">
        <span>{value}</span>
        <OpenNewWindow16 isColorInherited />
      </StyledLink>
    ) : (
      <ReadOnlyField>
        {hasError && <NegativeFill color={colors.negative100} />}
        {value}
      </ReadOnlyField>
    );

  return (
    <StyledFormField
      data-test={testTag}
      hasError={isEditModeVisible && hasError}
      labelText={<Label />}
      errorText={errorText}
      isHorizontalLayout
    >
      {isEditModeVisible ? (
        <InputFieldRow>
          <StyledInputText
            data-test={`${formatLabelForId(label)}-input`}
            hasError={hasError}
            onChange={setValue}
            placeholder={placeholder}
            value={value}
          />
          {!isEmbeddedMessagingEnabled && onDelete && (
            <StyledGhostButton data-test="custom-text-field-delete-button" onClick={onDelete}>
              <Delete />
            </StyledGhostButton>
          )}
        </InputFieldRow>
      ) : (
        <>
          {value ? (
            <ReadOnlyValue />
          ) : (
            <ReadOnlyField>
              <EmptyInput />
            </ReadOnlyField>
          )}
        </>
      )}
    </StyledFormField>
  );
};
